import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, set, update, onValue } from "firebase/database";

export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState("rules");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  
  // Library Settings
  const [settings, setSettings] = useState({
    finePerDay: 10,
    maxBooksPerTeacher: 5,
    issueDays: 7,
    lateFeePerDay: 10,
    maxRenewals: 2,
    weekendGrace: true
  });

  const role = localStorage.getItem("role");
  const userName = localStorage.getItem("userName") || "Admin";
  const adminId = localStorage.getItem("userId");
  const [adminPasswordInDb, setAdminPasswordInDb] = useState("admin9519");

  // Bottom navigation items
  const bottomNavItems = [
    { name: "Dashboard", path: "/admin-dashboard", icon: "🏠", activeIcon: "🏠" },
    { name: "Books", path: "/books", icon: "📚", activeIcon: "📚" },
    { name: "Teachers", path: "/teachers", icon: "👩‍🏫", activeIcon: "👩‍🏫" },
    { name: "Issue/Return", path: "/manage-books", icon: "🔄", activeIcon: "🔄" },
    { name: "Reports", path: "/reports", icon: "📊", activeIcon: "📊" },
    { name: "Settings", path: "/settings", icon: "⚙️", activeIcon: "⚙️" }
  ];

  // Fetch admin password from Firebase
  useEffect(() => {
    const adminRef = ref(db, "admin");
    const unsubscribe = onValue(adminRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.password) {
        setAdminPasswordInDb(data.password);
      } else {
        // Set default admin password in Firebase if not exists
        set(ref(db, "admin"), { password: "admin9519", updatedAt: new Date().toISOString() });
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch settings from Firebase
  useEffect(() => {
    const settingsRef = ref(db, "librarySettings");
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setSettings(data);
      } else {
        const defaultSettings = {
          finePerDay: 10,
          maxBooksPerTeacher: 5,
          issueDays: 7,
          lateFeePerDay: 10,
          maxRenewals: 2,
          weekendGrace: true,
          updatedAt: new Date().toISOString()
        };
        set(ref(db, "librarySettings"), defaultSettings);
        setSettings(defaultSettings);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Online/Offline handlers
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      showMessage("success", "Back online! Settings synced.");
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      showMessage("error", "You are offline. Changes will be saved when online.");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 4000);
  };

  // Save Settings to Firebase
  const saveSettings = async () => {
    setIsLoading(true);
    try {
      const updatedSettings = {
        ...settings,
        updatedAt: new Date().toISOString(),
        updatedBy: localStorage.getItem("userName") || "Admin"
      };
      
      await update(ref(db, "librarySettings"), updatedSettings);
      showMessage("success", "✅ Library settings saved successfully!");
      localStorage.setItem("librarySettings", JSON.stringify(updatedSettings));
      
    } catch (error) {
      console.error("Error saving settings:", error);
      showMessage("error", "Failed to save settings. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Change Password - Updates Firebase for Admin
  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showMessage("error", "Please fill all password fields");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage("error", "New password and confirm password do not match");
      return;
    }

    if (passwordData.newPassword.length < 4) {
      showMessage("error", "Password must be at least 4 characters");
      return;
    }

    setIsLoading(true);
    try {
      // For Admin - check against Firebase
      if (adminId === "misinternational") {
        // Verify current password from Firebase
        if (passwordData.currentPassword === adminPasswordInDb) {
          // Update admin password in Firebase
          await update(ref(db, "admin"), {
            password: passwordData.newPassword,
            updatedAt: new Date().toISOString(),
            updatedBy: userName
          });
          
          // Also update localStorage for current session
          localStorage.setItem("adminPassword", passwordData.newPassword);
          setAdminPasswordInDb(passwordData.newPassword);
          
          showMessage("success", "✅ Admin password changed successfully! Please use new password for next login.");
          setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
          setShowChangePassword(false);
        } else {
          showMessage("error", "Current password is incorrect");
        }
      } else {
        // For teachers in Firebase
        const userRef = ref(db, `users/${adminId}`);
        const snapshot = await get(userRef);
        const userData = snapshot.val();

        if (userData && userData.password === passwordData.currentPassword) {
          await update(userRef, {
            password: passwordData.newPassword,
            updatedAt: new Date().toISOString()
          });
          showMessage("success", "✅ Password changed successfully!");
          setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
          setShowChangePassword(false);
        } else {
          showMessage("error", "Current password is incorrect");
        }
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showMessage("error", "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/");
    }
  };

  // Access control
  if (role !== "admin") {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessDeniedCard}>
          <span style={styles.accessDeniedIcon}>🔒</span>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <button onClick={() => navigate("/admin-dashboard")} style={styles.backBtn}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Offline Banner */}
      {isOffline && (
        <div style={styles.offlineBanner}>
          ⚠️ You are offline. Changes will be saved when online.
        </div>
      )}

      {/* Message Alert */}
      {message.text && (
        <div style={{...styles.alert, ...(message.type === "success" ? styles.alertSuccess : styles.alertError)}}>
          {message.type === "success" ? "✅" : "❌"} {message.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>⚙️ Settings</h1>
          <p style={styles.subtitle}>Configure library rules and preferences</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div style={styles.adminAvatar}>
              {userName.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/profile"); setShowMobileMenu(false); }}>
            <span>👤</span> My Profile
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { setShowMobileMenu(false); }}>
            <span>⚙️</span> Settings
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}>
            <span>🚪</span> Logout
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button style={activeTab === "rules" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("rules")}>📋 Library Rules</button>
        <button style={activeTab === "manual" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("manual")}>📖 User Manual</button>
        <button style={activeTab === "about" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("about")}>ℹ️ About</button>
      </div>

      {/* Library Rules Tab */}
      {activeTab === "rules" && (
        <div style={styles.settingsCard}>
          <h2 style={styles.sectionTitle}>📋 Library Rules & Settings</h2>
          
          {/* Change Password Section */}
          <div style={styles.passwordSection}>
            <button onClick={() => setShowChangePassword(!showChangePassword)} style={styles.changePasswordBtn}>
              🔒 {showChangePassword ? "Cancel" : "Change Password"}
            </button>
            
            {showChangePassword && (
              <div style={styles.passwordForm}>
                <div style={styles.formGroup}>
                  <label>Current Password</label>
                  <input
                    type="password"
                    placeholder="Enter current password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>New Password</label>
                  <input
                    type="password"
                    placeholder="Enter new password (min 4 characters)"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <button onClick={handleChangePassword} style={styles.updatePasswordBtn} disabled={isLoading}>
                  {isLoading ? "Updating..." : "Update Password"}
                </button>
              </div>
            )}
          </div>

          <div style={styles.dividerLine}></div>

          <div style={styles.settingsGroup}>
            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <label style={styles.settingLabel}>💰 Fine per Day</label>
                <p style={styles.settingDescription}>Amount charged for each day of late return</p>
              </div>
              <div style={styles.settingControl}>
                <span style={styles.currencySymbol}>₹</span>
                <input type="number" min="0" max="100" value={settings.finePerDay || 10} onChange={(e) => handleSettingChange("finePerDay", parseInt(e.target.value))} style={styles.numberInput} />
                <span style={styles.unitText}>per day</span>
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <label style={styles.settingLabel}>📚 Max Books per Teacher</label>
                <p style={styles.settingDescription}>Maximum number of books a teacher can issue at once</p>
              </div>
              <div style={styles.settingControl}>
                <input type="number" min="1" max="20" value={settings.maxBooksPerTeacher || 5} onChange={(e) => handleSettingChange("maxBooksPerTeacher", parseInt(e.target.value))} style={styles.numberInput} />
                <span style={styles.unitText}>books</span>
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <label style={styles.settingLabel}>📅 Issue Duration</label>
                <p style={styles.settingDescription}>Number of days a book can be kept</p>
              </div>
              <div style={styles.settingControl}>
                <input type="number" min="1" max="30" value={settings.issueDays || 7} onChange={(e) => handleSettingChange("issueDays", parseInt(e.target.value))} style={styles.numberInput} />
                <span style={styles.unitText}>days</span>
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <label style={styles.settingLabel}>🔄 Maximum Renewals</label>
                <p style={styles.settingDescription}>How many times a book can be renewed</p>
              </div>
              <div style={styles.settingControl}>
                <input type="number" min="0" max="5" value={settings.maxRenewals || 2} onChange={(e) => handleSettingChange("maxRenewals", parseInt(e.target.value))} style={styles.numberInput} />
                <span style={styles.unitText}>times</span>
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <label style={styles.settingLabel}>🎉 Weekend Grace Period</label>
                <p style={styles.settingDescription}>Exclude weekends from fine calculation</p>
              </div>
              <div style={styles.settingControl}>
                <label style={styles.switch}>
                  <input type="checkbox" checked={settings.weekendGrace || true} onChange={(e) => handleSettingChange("weekendGrace", e.target.checked)} style={styles.checkbox} />
                  <span style={styles.slider}></span>
                </label>
                <span style={styles.unitText}>{settings.weekendGrace ? "Enabled" : "Disabled"}</span>
              </div>
            </div>
          </div>

          <div style={styles.infoBox}>
            <span style={styles.infoIcon}>ℹ️</span>
            <div style={styles.infoContent}>
              <strong>Current Rules Summary:</strong>
              <ul style={styles.infoList}>
                <li>Fine: ₹{settings.finePerDay || 10}/day for late returns</li>
                <li>Max books per teacher: {settings.maxBooksPerTeacher || 5} books</li>
                <li>Books issued for {settings.issueDays || 7} days</li>
                <li>Renewals allowed: {settings.maxRenewals || 2} times</li>
                <li>Weekend grace: {settings.weekendGrace ? "Active" : "Inactive"}</li>
              </ul>
            </div>
          </div>

          <button onClick={saveSettings} style={{...styles.saveBtn, ...(isLoading ? styles.disabledBtn : {})}} disabled={isLoading}>
            {isLoading ? <span style={styles.loaderContainer}><span style={styles.loader}></span>Saving...</span> : "💾 Save Settings"}
          </button>
        </div>
      )}

      {/* User Manual Tab */}
      {activeTab === "manual" && (
        <div style={styles.manualCard}>
          <h2 style={styles.sectionTitle}>📖 User Manual</h2>
          
          <div style={styles.manualSection}>
            <h3 style={styles.manualTitle}>👑 Admin Dashboard Guide</h3>
            <ul style={styles.manualList}>
              <li><strong>Dashboard Overview:</strong> View statistics (Total Books, Teachers, Issued Books, Pending Returns, Fine Collection)</li>
              <li><strong>Quick Actions:</strong> Direct access to Manage Teachers, Issue/Return Books, Manage Books, E-Library, Reports, Settings</li>
              <li><strong>Book Management:</strong> Add, edit, delete books with barcode scanning and generation</li>
              <li><strong>Teacher Management:</strong> Register teachers with unique ID, edit profiles, reset passwords</li>
              <li><strong>Issue/Return:</strong> Issue books to teachers, process returns with automatic fine calculation</li>
              <li><strong>E-Library:</strong> Add educational resources, Google Drive links, PDFs organized in folders</li>
              <li><strong>Reports:</strong> Generate transaction reports, export to CSV, print summaries</li>
              <li><strong>Settings:</strong> Configure library rules, change admin password</li>
            </ul>
          </div>

          <div style={styles.manualSection}>
            <h3 style={styles.manualTitle}>👩‍🏫 Teacher Dashboard Guide</h3>
            <ul style={styles.manualList}>
              <li><strong>My Books:</strong> View currently borrowed books with due dates</li>
              <li><strong>Borrowing History:</strong> Track previously returned books and fines paid</li>
              <li><strong>Due Date Alerts:</strong> Automatic notifications for upcoming and overdue books</li>
              <li><strong>Fine Calculation:</strong> Real-time fine calculation for late returns (₹{settings.finePerDay || 10}/day)</li>
              <li><strong>E-Library Access:</strong> Browse digital resources organized in folders by admin</li>
              <li><strong>Dictionary:</strong> Search English words with Hindi meanings</li>
              <li><strong>Profile Management:</strong> Update personal information and change password</li>
            </ul>
          </div>

          <div style={styles.tipBox}>
            <span style={styles.tipIcon}>💡</span>
            <div>
              <strong>Pro Tips:</strong>
              <ul style={styles.tipList}>
                <li>Use a barcode scanner for faster book lookup</li>
                <li>Regularly check overdue books report</li>
                <li>Keep teacher information updated with correct mobile numbers</li>
                <li>Configure library rules before starting operations</li>
                <li>Export transaction reports monthly for records</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* About Tab - Company Focused Only */}
      {activeTab === "about" && (
        <div style={styles.aboutCard}>
          <h2 style={styles.sectionTitle}>ℹ️ About</h2>
          
          <div style={styles.profileSection}>
            <div style={styles.companyLogo}>
              <span style={styles.companyIcon}>🏢</span>
            </div>
            <div style={styles.companyInfo}>
              <h3 style={styles.companyName}>Jauhari WebTech</h3>
              <p style={styles.companyTagline}>Innovative Web Solutions</p>
            </div>
          </div>

          <div style={styles.aboutSection}>
            <h3 style={styles.aboutTitle}>📞 Contact Information</h3>
            <div style={styles.contactInfo}>
              <p><span>📧</span> Email: <a href="mailto:alijauhari@jauhariwebtech.com" style={styles.contactLink}>alijauhari@jauhariwebtech.com</a></p>
              <p><span>📱</span> Phone: <a href="tel:+919628950249" style={styles.contactLink}>+91 9628950249</a></p>
              <p><span>🌐</span> Website: www.jauhariwebtech.com</p>
              <p><span>📍</span> Location: Ayodhya, India</p>
            </div>
          </div>

          <div style={styles.aboutSection}>
            <h3 style={styles.aboutTitle}>📱 About the Application</h3>
            <p><strong>MIS Library Management System</strong> is a comprehensive solution for managing library operations efficiently. Built with modern web technologies, it provides a seamless experience for administrators and teachers.</p>
          </div>

          <div style={styles.aboutSection}>
            <h3 style={styles.aboutTitle}>🛠️ Technologies Used</h3>
            <div style={styles.techGrid}>
              <span style={styles.techBadge}>React 18</span>
              <span style={styles.techBadge}>Firebase</span>
              <span style={styles.techBadge}>HTML5</span>
              <span style={styles.techBadge}>CSS3</span>
              <span style={styles.techBadge}>JavaScript ES6+</span>
              <span style={styles.techBadge}>PWA Ready</span>
            </div>
          </div>

          <div style={styles.aboutSection}>
            <h3 style={styles.aboutTitle}>✨ Key Features</h3>
            <ul style={styles.featureList}>
              <li>✅ Barcode scanning and generation</li>
              <li>✅ Real-time data synchronization</li>
              <li>✅ Offline support with caching</li>
              <li>✅ Automatic fine calculation</li>
              <li>✅ Printable barcode labels</li>
              <li>✅ Mobile responsive design</li>
              <li>✅ Export reports to CSV</li>
              <li>✅ Teacher and book management</li>
              <li>✅ E-Library with folder organization</li>
            </ul>
          </div>

          <div style={styles.aboutSection}>
            <h3 style={styles.aboutTitle}>📄 Version Information</h3>
            <p><strong>Version:</strong> 2.0.0</p>
            <p><strong>Release Date:</strong> January 2026</p>
            <p><strong>License:</strong> MIT</p>
          </div>

          <div style={styles.creditBox}>
            <p>© 2026 Jauhari WebTech. All Rights Reserved.</p>
            <p>Developed by <strong>Jauhari WebTech</strong></p>
          </div>
        </div>
      )}

      {/* Logout Button */}
      <button onClick={handleLogout} style={styles.logoutButton}>
        <span>🚪</span> Logout from System
      </button>

      {/* Bottom Navigation Bar */}
      <div style={styles.bottomNav}>
        {bottomNavItems.map((item) => (
          <div key={item.path} style={{...styles.bottomNavItem, ...(location.pathname === item.path && styles.bottomNavItemActive)}} onClick={() => handleNavigation(item.path)}>
            <div style={styles.bottomNavIcon}>{location.pathname === item.path ? item.activeIcon : item.icon}</div>
            <span style={styles.bottomNavLabel}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: "1200px", margin: "0 auto", padding: "clamp(16px, 4vw, 32px)", paddingBottom: "80px", fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif", backgroundColor: "#f7fafc", minHeight: "100vh" , overflowY: "auto",WebkitOverflowScrolling: "touch"},
 offlineBanner: { background: "#ed8936", color: "#fff", padding: "12px", textAlign: "center", borderRadius: "8px", marginBottom: "20px", fontWeight: "500" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "24px" },
  title: { fontSize: "clamp(24px, 5vw, 32px)", fontWeight: "700", margin: "0 0 8px 0", color: "#2d3748" },
  subtitle: { fontSize: "14px", color: "#718096", margin: 0 },
  headerRight: { display: "flex", alignItems: "center", gap: "16px" },
  adminProfile: { cursor: "pointer" },
  adminAvatar: { width: "40px", height: "40px", borderRadius: "50%", background: "linear-gradient(135deg, #667eea, #764ba2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "bold", fontSize: "18px" },
  mobileMenu: { position: "absolute", top: "70px", right: "20px", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 1000, minWidth: "160px", overflow: "hidden" },
  mobileMenuItem: { padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px", fontSize: "14px", color: "#2d3748", cursor: "pointer", transition: "background 0.2s ease", borderBottom: "1px solid #e2e8f0" },
  alert: { padding: "14px 18px", borderRadius: "12px", marginBottom: "20px", fontSize: "14px", fontWeight: "500" },
  alertSuccess: { backgroundColor: "#c6f6d5", color: "#22543d", border: "1px solid #9ae6b4" },
  alertError: { backgroundColor: "#fed7d7", color: "#742a2a", border: "1px solid #fc8181" },
  tabContainer: { display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap", backgroundColor: "#fff", padding: "6px", borderRadius: "14px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  tab: { flex: 1, padding: "12px 20px", backgroundColor: "transparent", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "all 0.3s ease", color: "#64748b", minWidth: "120px" },
  activeTab: { flex: 1, padding: "12px 20px", backgroundColor: "#667eea", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "all 0.3s ease", color: "#fff", minWidth: "120px" },
  settingsCard: { backgroundColor: "#fff", borderRadius: "20px", padding: "clamp(20px, 5vw, 32px)", marginBottom: "24px", boxShadow: "0 4px 6px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0" },
  manualCard: { backgroundColor: "#fff", borderRadius: "20px", padding: "clamp(20px, 5vw, 32px)", marginBottom: "24px", boxShadow: "0 4px 6px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0" },
  aboutCard: { backgroundColor: "#fff", borderRadius: "20px", padding: "clamp(20px, 5vw, 32px)", marginBottom: "24px", boxShadow: "0 4px 6px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0" },
  sectionTitle: { fontSize: "clamp(20px, 4vw, 24px)", fontWeight: "600", margin: "0 0 24px 0", color: "#2d3748", borderBottom: "2px solid #e2e8f0", paddingBottom: "12px" },
  passwordSection: { marginBottom: "24px" },
  changePasswordBtn: { padding: "10px 20px", backgroundColor: "#667eea", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer", marginBottom: "16px" },
  passwordForm: { padding: "20px", backgroundColor: "#f7fafc", borderRadius: "12px", marginTop: "12px" },
  formGroup: { marginBottom: "16px" },
  input: { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "2px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box" },
  updatePasswordBtn: { width: "100%", padding: "12px", backgroundColor: "#48bb78", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  dividerLine: { height: "1px", backgroundColor: "#e2e8f0", margin: "24px 0" },
  settingsGroup: { display: "flex", flexDirection: "column", gap: "24px", marginBottom: "32px" },
  settingItem: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", padding: "16px", backgroundColor: "#f7fafc", borderRadius: "12px" },
  settingInfo: { flex: 1 },
  settingLabel: { fontSize: "16px", fontWeight: "600", color: "#2d3748", display: "block", marginBottom: "4px" },
  settingDescription: { fontSize: "12px", color: "#718096", margin: 0 },
  settingControl: { display: "flex", alignItems: "center", gap: "8px" },
  numberInput: { width: "80px", padding: "8px", borderRadius: "8px", border: "2px solid #e2e8f0", fontSize: "14px", textAlign: "center" },
  currencySymbol: { fontSize: "16px", fontWeight: "600", color: "#4a5568" },
  unitText: { fontSize: "12px", color: "#718096" },
  switch: { position: "relative", display: "inline-block", width: "50px", height: "24px" },
  checkbox: { opacity: 0, width: 0, height: 0 },
  slider: { position: "absolute", cursor: "pointer", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "#ccc", transition: "0.3s", borderRadius: "24px" },
  infoBox: { display: "flex", gap: "16px", padding: "16px", backgroundColor: "#ebf8ff", borderRadius: "12px", marginBottom: "24px", border: "1px solid #bee3f8" },
  infoIcon: { fontSize: "24px" },
  infoContent: { flex: 1, fontSize: "13px", color: "#2c5282" },
  infoList: { margin: "8px 0 0 0", paddingLeft: "20px", lineHeight: "1.6" },
  saveBtn: { width: "100%", padding: "14px", backgroundColor: "#48bb78", color: "#fff", border: "none", borderRadius: "12px", fontSize: "16px", fontWeight: "600", cursor: "pointer", transition: "all 0.3s ease" },
  disabledBtn: { opacity: 0.6, cursor: "not-allowed" },
  loaderContainer: { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  loader: { width: "18px", height: "18px", border: "2px solid #fff", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  manualSection: { marginBottom: "24px" },
  manualTitle: { fontSize: "18px", fontWeight: "600", color: "#2d3748", margin: "0 0 12px 0" },
  manualList: { margin: "0", paddingLeft: "20px", lineHeight: "1.8", color: "#4a5568" },
  tipBox: { display: "flex", gap: "12px", padding: "16px", backgroundColor: "#fefcbf", borderRadius: "12px", marginTop: "24px", border: "1px solid #fefcbf" },
  tipIcon: { fontSize: "20px" },
  tipList: { margin: "8px 0 0 0", paddingLeft: "20px", lineHeight: "1.6" },
  profileSection: { display: "flex", alignItems: "center", gap: "20px", marginBottom: "32px", padding: "20px", backgroundColor: "#f7fafc", borderRadius: "16px", flexWrap: "wrap" },
  companyLogo: { width: "80px", height: "80px", borderRadius: "50%", background: "linear-gradient(135deg, #667eea, #764ba2)", display: "flex", alignItems: "center", justifyContent: "center" },
  companyIcon: { fontSize: "40px" },
  companyInfo: { flex: 1 },
  companyName: { fontSize: "24px", fontWeight: "700", margin: "0 0 4px 0", color: "#2d3748" },
  companyTagline: { fontSize: "14px", color: "#718096", margin: 0 },
  aboutSection: { marginBottom: "24px" },
  aboutTitle: { fontSize: "16px", fontWeight: "600", color: "#2d3748", margin: "0 0 12px 0" },
  contactInfo: { display: "flex", flexDirection: "column", gap: "8px" },
  contactLink: { color: "#667eea", textDecoration: "none" },
  techGrid: { display: "flex", flexWrap: "wrap", gap: "10px" },
  techBadge: { padding: "6px 12px", backgroundColor: "#edf2f7", borderRadius: "20px", fontSize: "12px", color: "#4a5568" },
  featureList: { margin: "0", paddingLeft: "20px", lineHeight: "1.8", color: "#4a5568" },
  creditBox: { textAlign: "center", padding: "20px", backgroundColor: "#f7fafc", borderRadius: "12px", marginTop: "24px", color: "#718096", fontSize: "13px" },
  logoutButton: { width: "100%", padding: "14px", backgroundColor: "#e53e3e", color: "#fff", border: "none", borderRadius: "12px", fontSize: "16px", fontWeight: "600", cursor: "pointer", transition: "all 0.3s ease", marginBottom: "24px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  bottomNav: { position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: "#fff", display: "flex", justifyContent: "space-around", alignItems: "center", padding: "8px 16px", boxShadow: "0 -2px 10px rgba(0,0,0,0.1)", zIndex: 100, borderTop: "1px solid #e2e8f0" },
  bottomNavItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", cursor: "pointer", padding: "6px 12px", borderRadius: "8px", transition: "all 0.2s ease", flex: 1 },
  bottomNavItemActive: { color: "#667eea" },
  bottomNavIcon: { fontSize: "22px" },
  bottomNavLabel: { fontSize: "11px", fontWeight: "500" },
  accessDenied: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
  accessDeniedCard: { textAlign: "center", padding: "40px", background: "#fff", borderRadius: "16px", maxWidth: "400px", margin: "20px" },
  accessDeniedIcon: { fontSize: "64px", display: "block", marginBottom: "16px" },
  backBtn: { marginTop: "20px", padding: "12px 24px", background: "#667eea", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", cursor: "pointer" }
};

// Add animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  input:focus { outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1); }
  button:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.05); }
  .logout-button:hover { background-color: #c53030; }
  .contact-link:hover { text-decoration: underline; }
  @media (max-width: 768px) {
    .setting-item { flex-direction: column; align-items: flex-start; }
    .tab-container { flex-direction: column; }
    .tab, .active-tab { width: 100%; }
    .profile-section { flex-direction: column; text-align: center; }
  }
`;
document.head.appendChild(styleSheet);